import React from 'react';
import { Box } from '@mantine/core';
import { IconPlayerPlay, IconTable } from '@tabler/icons-react';
import { PluginFunctionRunner } from '../utilities/PluginFunctionRunner';
import DynamicEntityManagement from '../entities/DynamicEntityManagement';
import { ManagementTabsView, type ManagementTabDefinition } from '../common';

export interface PluginFunctionsManagementProps {
    /** Tab to show from route: 0 = Invoke, 1 = Table view */
    tabFromRoute?: 0 | 1;
    /** Called when user switches tab; caller should update hash to admin/pluginfunctions/invoke or .../table */
    onTabChange?: (tab: 0 | 1) => void;
    /** When set, show $pivot list for this plugin (Invoke tab deep link) */
    pluginFunctionIdFromRoute?: string | null;
    /** When set, show $pivot/{entityId} result (Invoke tab deep link) */
    pivotEntityIdFromRoute?: string | null;
    /** When set, show $pivot/{entityId}/$details/{detailId} result (Invoke tab deep link) */
    pivotDetailIdFromRoute?: string | null;
    /** When set, show Prepare Invoke view (form from function catalog entry) */
    prepareCatalogIdFromRoute?: string | null;
    /** When set with prepare, id of the thing selected before (detail id); used as last segment of invoke POST */
    prepareDetailIdFromRoute?: string | null;
    authResolved?: boolean;
    onBehalfOfKey?: string | null;
    /** For Table view tab: selected entity id when opening a specific row */
    selectedEntityId?: string | null;
    /** For Table view tab: view/edit mode for the selected entity */
    selectedEntityViewMode?: 'view' | 'edit' | 'create' | null;
    /** For Table view tab: entity type for deep link */
    deepLinkEntityType?: string | null;
}

export const PluginFunctionsManagement: React.FC<PluginFunctionsManagementProps> = ({
    tabFromRoute = 0,
    onTabChange,
    pluginFunctionIdFromRoute = null,
    pivotEntityIdFromRoute = null,
    pivotDetailIdFromRoute = null,
    prepareCatalogIdFromRoute = null,
    prepareDetailIdFromRoute = null,
    authResolved = false,
    onBehalfOfKey = null,
    selectedEntityId = null,
    selectedEntityViewMode = null,
    deepLinkEntityType = null
}) => {
    const handleTabChange = (tabId: string) => {
        onTabChange?.(tabId === 'table' ? 1 : 0);
    };

    const tabs: ManagementTabDefinition[] = [
        {
            id: 'invoke',
            label: 'Invoke',
            icon: IconPlayerPlay,
            render: () => (
                <PluginFunctionRunner
                    pluginFunctionIdFromRoute={pluginFunctionIdFromRoute}
                    pivotEntityIdFromRoute={pivotEntityIdFromRoute}
                    pivotDetailIdFromRoute={pivotDetailIdFromRoute}
                    prepareCatalogIdFromRoute={prepareCatalogIdFromRoute}
                    prepareDetailIdFromRoute={prepareDetailIdFromRoute}
                    basePath="admin/pluginfunctions/invoke"
                />
            )
        },
        {
            id: 'table',
            label: 'Table view',
            icon: IconTable,
            render: () => (
                <DynamicEntityManagement
                    entityType="PluginFunction"
                    selectedEntityId={selectedEntityId}
                    selectedEntityViewMode={selectedEntityViewMode}
                    deepLinkEntityType={deepLinkEntityType}
                    authResolved={authResolved}
                    onBehalfOfKey={onBehalfOfKey ?? undefined}
                    listViewHashBase="admin/pluginfunctions/table"
                />
            )
        }
    ];

    return (
        <Box w="100%">
            <ManagementTabsView
                tabId={tabFromRoute === 1 ? 'table' : 'invoke'}
                onTabChange={handleTabChange}
                tabs={tabs}
            />
        </Box>
    );
};
